import { hasNativeJSBridge } from '@/utils/bridge-runtime';

export type OnboardingStatus =
  | 'invite_required'
  | 'in_progress'
  | 'offered'
  | 'completed';

export type OnboardingRouteRuntime = 'web' | 'native';

export type OnboardingStateResponse = {
  onboarding_status: OnboardingStatus;
  onboarding_complete: boolean;
  onboarding_conversation_id?: string | null;
  active_offer_id?: string | null;
};

export type OnboardingConversationSummary = {
  id: string;
  object: 'conversation';
  created_at: number;
  metadata?: {
    title?: string | null;
  };
};

export type OnboardingConversationItem = Record<string, unknown>;

export type OnboardingConversationItemList = {
  object: 'list';
  first_id?: string;
  last_id?: string;
  has_more: boolean;
  data: OnboardingConversationItem[];
};

export type OnboardingOfferResponse = {
  id: string;
  status: string;
  offered_plan_tier: string;
  discounted_monthly_plan_code: string;
  offered_price: number;
  offered_currency: string;
  discount_semantics: string;
  stripe_promotion_code_id?: string | null;
  active_checkout_session_id?: string | null;
  active_checkout_session_url?: string | null;
};

export type EstimatePricingToolOffer = {
  offer_id: string;
  status: string;
  offered_plan_tier: string;
  discounted_monthly_plan_code: string;
  offered_price: number;
  offered_currency: string;
  discount_semantics: string;
  pricing_reason_summary?: string | null;
  pricing_confidence?: string | null;
  recommended_discount_percent?: number | null;
};

export type EstimatePricingToolPlan = {
  plan_code: string;
  plan_tier: string;
  monthly_price: number;
  currency: string;
  discount_percent?: number | null;
  discounted_price?: number | null;
};

export type EstimatePricingToolResult = {
  type: 'estimate_pricing_result';
  already_estimated: boolean;
  demo_discount_unlocked?: boolean;
  message: string;
  plans?: EstimatePricingToolPlan[];
  offer?: EstimatePricingToolOffer;
  checkout?: {
    action: 'open_checkout';
  };
};

export type ShowDownloadButtonToolResult = {
  type: 'show_download_button_result';
  label: string;
  variant: 'primary' | 'secondary';
  action?: 'open_bridge_or_download';
};

export type GuideDemoSessionsToolResult = {
  type: 'guide_demo_sessions_result';
  demo_session_ids: string[];
};

export type CurrentSubscriptionGiftCard = {
  variant: string;
  title: string;
  body: string;
  image_url: string;
  alt: string;
};

export type OnboardingImageReason =
  | 'after_gift_card'
  | 'checkout_success'
  | 'active_subscription';

export type OnboardingImageResult = {
  type: 'onboarding_image_result';
  reason: OnboardingImageReason | string;
  image_url: string;
  alt: string;
};

export type CurrentSubscriptionToolResult = {
  type: 'current_subscription_result';
  active: boolean;
  has_subscription: boolean;
  has_account_entitlement?: boolean;
  source?: string | null;
  entitlement_kind?: string | null;
  plan_code?: string | null;
  status?: string | null;
  message: string;
  gift_card?: CurrentSubscriptionGiftCard;
  followup_image?: OnboardingImageResult;
};

export type OnboardingToolResults = {
  estimatePricing: EstimatePricingToolResult | null;
  downloadButton: ShowDownloadButtonToolResult | null;
  guideDemoSessions: GuideDemoSessionsToolResult | null;
};

export type OnboardingCheckoutResponse = {
  offer: OnboardingOfferResponse;
  session: {
    id: string;
    url: string;
    expires_at?: string | null;
  };
};

export type OnboardingResponsesAPIResponse = {
  id: string;
  object?: string;
  status: string;
  model?: string;
  output?: Array<{
    type?: string;
    id?: string;
    role?: string;
    content?: Array<{
      type?: string;
      text?: string;
    }>;
    output?: unknown;
    call_id?: string;
  }>;
  usage?: {
    input_tokens?: number;
    output_tokens?: number;
    total_tokens?: number;
  };
};

type RequestInitWithBody = RequestInit & {
  body?: BodyInit | null;
};

type OnboardingGateErrorBody = {
  error?: {
    code?: string;
    message?: string;
    details?: {
      onboarding_status?: OnboardingStatus;
    };
  };
};

export function readOnboardingClientLanguage() {
  if (typeof navigator === 'undefined') {
    return undefined;
  }
  if (navigator.languages?.length) {
    return navigator.languages.join(',');
  }
  return navigator.language || undefined;
}

export class OnboardingRequestError extends Error {
  readonly onboardingStatus?: OnboardingStatus;

  constructor(
    readonly status: number,
    message: string,
    options?: {
      onboardingStatus?: OnboardingStatus;
    }
  ) {
    super(message);
    this.name = 'OnboardingRequestError';
    this.onboardingStatus = options?.onboardingStatus;
  }
}

const jsonHeaders = {
  accept: 'application/json',
  'content-type': 'application/json',
};

function getOnboardingCompleteCacheKey(userId: string) {
  return `${userId}:onboarding`;
}

export function hasCachedOnboardingComplete(userId: string): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  return (
    window.localStorage.getItem(getOnboardingCompleteCacheKey(userId)) ===
    'true'
  );
}

export function writeOnboardingCompleteCache(
  userId: string,
  completed: boolean
) {
  if (typeof window === 'undefined') {
    return;
  }

  const key = getOnboardingCompleteCacheKey(userId);
  if (completed) {
    window.localStorage.setItem(key, 'true');
  } else {
    window.localStorage.removeItem(key);
  }
}

function normalizeNextPath(next: string | null | undefined): string {
  if (!next || !next.startsWith('/')) {
    return '/chat';
  }
  if (next === '/login' || next.startsWith('/login?')) {
    return '/chat';
  }
  if (next === '/onboarding' || next.startsWith('/onboarding/')) {
    return '/chat';
  }
  return next;
}

function pathWithNext(path: string, next: string) {
  return `${path}?next=${encodeURIComponent(normalizeNextPath(next))}`;
}

export function resolveWebOnboardingDownloadPath(next?: string | null): string {
  return pathWithNext('/onboarding/download', normalizeNextPath(next));
}

export function resolveOnboardingPathForState(
  state: OnboardingStateResponse,
  next?: string | null,
  options?: { runtime?: OnboardingRouteRuntime }
): string {
  const normalizedNext = normalizeNextPath(next);
  const runtime = options?.runtime ?? 'web';

  switch (state.onboarding_status) {
    case 'completed':
      return normalizedNext;
    case 'in_progress':
    case 'offered':
      return runtime === 'native'
        ? pathWithNext('/onboarding', normalizedNext)
        : pathWithNext('/onboarding/download', normalizedNext);
    case 'invite_required':
    default:
      return pathWithNext('/onboarding/waitlist', normalizedNext);
  }
}

async function readErrorBody(response: Response): Promise<unknown> {
  const contentType = response.headers.get('content-type') ?? '';
  if (contentType.includes('application/json')) {
    return response.json().catch(() => null);
  }

  return response.text().catch(() => null);
}

function getErrorMessage(status: number, body: unknown, fallback: string) {
  if (
    typeof body === 'object' &&
    body !== null &&
    'error' in body &&
    typeof (body as OnboardingGateErrorBody).error?.message === 'string'
  ) {
    return (body as OnboardingGateErrorBody).error?.message ?? fallback;
  }

  if (typeof body === 'string' && body.trim()) {
    return body.trim();
  }

  return fallback || `Request failed (${status})`;
}

function getErrorOnboardingStatus(body: unknown): OnboardingStatus | undefined {
  if (typeof body !== 'object' || body === null) {
    return undefined;
  }

  const status = (body as OnboardingGateErrorBody).error?.details
    ?.onboarding_status;
  return status;
}

function hasNativeOnboardingBridge(): boolean {
  return hasNativeJSBridge();
}

function withOnboardingRuntimeHeaders(
  headers: HeadersInit | undefined
): HeadersInit | undefined {
  if (!hasNativeOnboardingBridge()) {
    return headers;
  }

  return {
    ...headers,
    'X-Cueboard-Platform': 'macos',
    'X-Cueboard-Surface': 'onboarding',
  };
}

async function fetchOnboardingJSON<T>(
  path: string,
  init?: RequestInitWithBody
): Promise<T> {
  const response = await fetch(path, {
    credentials: 'include',
    ...init,
    headers: withOnboardingRuntimeHeaders(init?.headers),
  });

  if (!response.ok) {
    const body = await readErrorBody(response);
    throw new OnboardingRequestError(
      response.status,
      getErrorMessage(response.status, body, 'Onboarding request failed'),
      {
        onboardingStatus: getErrorOnboardingStatus(body),
      }
    );
  }

  return (await response.json()) as T;
}

export async function getOnboardingState(): Promise<OnboardingStateResponse> {
  return fetchOnboardingJSON<OnboardingStateResponse>(
    '/v1/user/onboarding/state',
    {
      method: 'GET',
      headers: {
        accept: 'application/json',
      },
    }
  );
}

export async function applyOnboardingInviteCode(
  code: string,
  clientLanguage = readOnboardingClientLanguage()
): Promise<OnboardingStateResponse> {
  const payload: { code: string; client_language?: string } = { code };
  if (clientLanguage?.trim()) {
    payload.client_language = clientLanguage.trim();
  }

  return fetchOnboardingJSON<OnboardingStateResponse>(
    '/v1/user/onboarding/invite-code',
    {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify(payload),
    }
  );
}

export async function createOnboardingConversation(
  items?: unknown[],
  clientLanguage = readOnboardingClientLanguage()
): Promise<OnboardingConversationSummary> {
  const payload: { items?: unknown[]; client_language?: string } = {};
  if (items) {
    payload.items = items;
  }
  if (clientLanguage?.trim()) {
    payload.client_language = clientLanguage.trim();
  }

  return fetchOnboardingJSON<OnboardingConversationSummary>(
    '/v1/user/onboarding/conversations',
    {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify(payload),
    }
  );
}

export async function getOnboardingConversationItems(
  conversationId: string
): Promise<OnboardingConversationItemList> {
  return fetchOnboardingJSON<OnboardingConversationItemList>(
    `/v1/user/onboarding/conversations/${conversationId}/items`,
    {
      method: 'GET',
      headers: {
        accept: 'application/json',
      },
    }
  );
}

export async function addOnboardingConversationItems(
  conversationId: string,
  items: unknown[]
): Promise<OnboardingConversationItemList> {
  return fetchOnboardingJSON<OnboardingConversationItemList>(
    `/v1/user/onboarding/conversations/${conversationId}/items`,
    {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify({ items }),
    }
  );
}

export async function createOnboardingCheckout(input: {
  plan_code?: string;
  success_url: string;
  cancel_url: string;
  force_new?: boolean;
}): Promise<OnboardingCheckoutResponse> {
  return fetchOnboardingJSON<OnboardingCheckoutResponse>(
    '/v1/user/onboarding/checkout',
    {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify(input),
    }
  );
}

export function userInputTextMessage(text: string) {
  return {
    type: 'message',
    role: 'user',
    content: [{ type: 'input_text', text }],
  };
}

export function readOnboardingResponseText(
  response: OnboardingResponsesAPIResponse | null | undefined
): string {
  for (const output of response?.output ?? []) {
    if (output.role !== 'assistant') {
      continue;
    }
    for (const part of output.content ?? []) {
      if (part.type === 'output_text' && typeof part.text === 'string') {
        return part.text;
      }
    }
  }
  return '';
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return null;
  }
  return value as Record<string, unknown>;
}

function parseJSONRecord(value: unknown): Record<string, unknown> | null {
  const record = asRecord(value);
  if (record) {
    return record;
  }

  if (typeof value !== 'string' || !value.trim()) {
    return null;
  }

  try {
    return asRecord(JSON.parse(value));
  } catch {
    return null;
  }
}

function readOptionalString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function readOptionalNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function parseEstimatePricingToolResult(
  record: Record<string, unknown>
): EstimatePricingToolResult | null {
  if (record.type !== 'estimate_pricing_result') {
    return null;
  }

  const offer = asRecord(record.offer);
  const checkout = asRecord(record.checkout);
  const plans = Array.isArray(record.plans)
    ? record.plans.flatMap(plan => {
        const planRecord = asRecord(plan);
        const planCode = readOptionalString(planRecord?.plan_code);
        const planTier = readOptionalString(planRecord?.plan_tier);
        const monthlyPrice = readOptionalNumber(planRecord?.monthly_price);
        const currency = readOptionalString(planRecord?.currency);
        if (!planCode || !planTier || monthlyPrice === null || !currency) {
          return [];
        }
        return [
          {
            plan_code: planCode,
            plan_tier: planTier,
            monthly_price: monthlyPrice,
            currency,
            discount_percent: readOptionalNumber(planRecord?.discount_percent),
            discounted_price: readOptionalNumber(planRecord?.discounted_price),
          },
        ];
      })
    : [];

  let parsedOffer: EstimatePricingToolOffer | undefined;
  if (offer) {
    const offerId = readOptionalString(offer.offer_id);
    const status = readOptionalString(offer.status);
    const offeredPlanTier = readOptionalString(offer.offered_plan_tier);
    const discountedMonthlyPlanCode = readOptionalString(
      offer.discounted_monthly_plan_code
    );
    const offeredPrice = readOptionalNumber(offer.offered_price);
    const offeredCurrency = readOptionalString(offer.offered_currency);
    const discountSemantics = readOptionalString(offer.discount_semantics);

    if (
      offerId &&
      status &&
      offeredPlanTier &&
      discountedMonthlyPlanCode &&
      offeredPrice !== null &&
      offeredCurrency &&
      discountSemantics
    ) {
      parsedOffer = {
        offer_id: offerId,
        status,
        offered_plan_tier: offeredPlanTier,
        discounted_monthly_plan_code: discountedMonthlyPlanCode,
        offered_price: offeredPrice,
        offered_currency: offeredCurrency,
        discount_semantics: discountSemantics,
        pricing_reason_summary: readOptionalString(
          offer.pricing_reason_summary
        ),
        pricing_confidence: readOptionalString(offer.pricing_confidence),
        recommended_discount_percent: readOptionalNumber(
          offer.recommended_discount_percent
        ),
      };
    }
  }

  if (!parsedOffer && plans.length === 0) {
    return null;
  }

  return {
    type: 'estimate_pricing_result',
    already_estimated: record.already_estimated === true,
    demo_discount_unlocked: record.demo_discount_unlocked === true,
    message:
      readOptionalString(record.message) ??
      'Your personalized onboarding offer is ready.',
    plans,
    offer: parsedOffer,
    checkout:
      checkout?.action === 'open_checkout'
        ? { action: 'open_checkout' }
        : undefined,
  };
}

function parseShowDownloadButtonToolResult(
  record: Record<string, unknown>
): ShowDownloadButtonToolResult | null {
  if (record.type !== 'show_download_button_result') {
    return null;
  }

  return {
    type: 'show_download_button_result',
    label: readOptionalString(record.label) ?? 'Download Bridge',
    variant: record.variant === 'secondary' ? 'secondary' : 'primary',
    action:
      record.action === 'open_bridge_or_download'
        ? 'open_bridge_or_download'
        : undefined,
  };
}

function parseGuideDemoSessionsToolResult(
  record: Record<string, unknown>
): GuideDemoSessionsToolResult | null {
  if (record.type !== 'guide_demo_sessions_result') {
    return null;
  }
  if (!Array.isArray(record.demo_session_ids)) {
    return null;
  }

  const ids = Array.from(
    new Set(
      record.demo_session_ids
        .filter((id): id is string => typeof id === 'string')
        .map(id => id.trim())
        .filter(Boolean)
    )
  );
  if (ids.length === 0) {
    return null;
  }

  return {
    type: 'guide_demo_sessions_result',
    demo_session_ids: ids,
  };
}

function parseOnboardingImagePayload(
  record: Record<string, unknown> | null | undefined,
  options?: {
    requireType?: boolean;
  }
): OnboardingImageResult | undefined {
  if (!record) {
    return undefined;
  }
  if (options?.requireType && record.type !== 'onboarding_image_result') {
    return undefined;
  }

  const imageURL = readOptionalString(record.image_url);
  const alt = readOptionalString(record.alt);
  const reason = readOptionalString(record.reason);
  if (!imageURL || !alt || !reason) {
    return undefined;
  }

  return {
    type: 'onboarding_image_result',
    reason,
    image_url: imageURL,
    alt,
  };
}

function parseOnboardingImageResult(
  record: Record<string, unknown>
): OnboardingImageResult | null {
  return parseOnboardingImagePayload(record, { requireType: true }) ?? null;
}

function parseCurrentSubscriptionToolResult(
  record: Record<string, unknown>
): CurrentSubscriptionToolResult | null {
  if (record.type !== 'current_subscription_result') {
    return null;
  }

  const giftCard = asRecord(record.gift_card);
  const title = readOptionalString(giftCard?.title);
  const body = readOptionalString(giftCard?.body);
  const imageURL = readOptionalString(giftCard?.image_url);
  const alt = readOptionalString(giftCard?.alt);
  const parsedGiftCard =
    title && body && imageURL && alt
      ? {
          variant: readOptionalString(giftCard?.variant) ?? 'invite_gift',
          title,
          body,
          image_url: imageURL,
          alt,
        }
      : undefined;
  const followupImage = parseOnboardingImagePayload(
    asRecord(record.followup_image)
  );

  return {
    type: 'current_subscription_result',
    active: record.active === true,
    has_subscription: record.has_subscription === true,
    has_account_entitlement: record.has_account_entitlement === true,
    source: readOptionalString(record.source),
    entitlement_kind: readOptionalString(record.entitlement_kind),
    plan_code: readOptionalString(record.plan_code),
    status: readOptionalString(record.status),
    message: readOptionalString(record.message) ?? '',
    gift_card: parsedGiftCard,
    followup_image: followupImage,
  };
}

export function parseOnboardingToolResult(
  value: unknown
):
  | EstimatePricingToolResult
  | ShowDownloadButtonToolResult
  | GuideDemoSessionsToolResult
  | CurrentSubscriptionToolResult
  | OnboardingImageResult
  | null {
  const record = parseJSONRecord(value);
  if (!record) {
    return null;
  }

  return (
    parseEstimatePricingToolResult(record) ??
    parseShowDownloadButtonToolResult(record) ??
    parseGuideDemoSessionsToolResult(record) ??
    parseCurrentSubscriptionToolResult(record) ??
    parseOnboardingImageResult(record)
  );
}

export function readOnboardingToolResults(
  items: Array<Record<string, unknown>>,
  options?: {
    activeOfferId?: string | null;
  }
): OnboardingToolResults {
  const activeOfferId = options?.activeOfferId?.trim() || null;
  let estimatePricing: EstimatePricingToolResult | null = null;
  let matchingEstimatePricing: EstimatePricingToolResult | null = null;
  let downloadButton: ShowDownloadButtonToolResult | null = null;
  let guideDemoSessions: GuideDemoSessionsToolResult | null = null;

  for (const item of items) {
    if (item.type !== 'function_call_output') {
      continue;
    }

    const result = parseOnboardingToolResult(item.output);
    if (!result) {
      continue;
    }

    if (result.type === 'estimate_pricing_result') {
      estimatePricing = result;
      if (activeOfferId && result.offer?.offer_id === activeOfferId) {
        matchingEstimatePricing = result;
      }
      continue;
    }

    if (result.type === 'show_download_button_result') {
      downloadButton = result;
      continue;
    }

    if (result.type === 'guide_demo_sessions_result') {
      guideDemoSessions = result;
    }
  }

  return {
    estimatePricing: matchingEstimatePricing ?? estimatePricing,
    downloadButton,
    guideDemoSessions,
  };
}

export function readGuidedDemoSessionIdsFromItems(
  items: Array<Record<string, unknown>>
): string[] {
  const ids: string[] = [];
  for (const item of items) {
    if (item.type !== 'function_call_output') {
      continue;
    }
    const result = parseOnboardingToolResult(item.output);
    if (result?.type !== 'guide_demo_sessions_result') {
      continue;
    }
    ids.push(...result.demo_session_ids);
  }
  return Array.from(new Set(ids));
}

type StreamHandlers = {
  signal?: AbortSignal;
  onTextDelta?: (delta: string) => void;
  onCompleted?: (response: OnboardingResponsesAPIResponse | null) => void;
};

function parseSSEPayload(raw: string): unknown {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

async function readOnboardingResponseError(response: Response): Promise<never> {
  const body = await readErrorBody(response);
  throw new OnboardingRequestError(
    response.status,
    getErrorMessage(response.status, body, 'Failed to create response'),
    {
      onboardingStatus: getErrorOnboardingStatus(body),
    }
  );
}

export async function streamOnboardingResponse(
  text: string,
  handlers: StreamHandlers = {},
  clientLanguage = readOnboardingClientLanguage()
): Promise<OnboardingResponsesAPIResponse | null> {
  const payload: {
    stream: true;
    input: ReturnType<typeof userInputTextMessage>[];
    client_language?: string;
  } = {
    stream: true,
    input: [userInputTextMessage(text)],
  };
  if (clientLanguage?.trim()) {
    payload.client_language = clientLanguage.trim();
  }

  const response = await fetch('/v1/user/onboarding/responses', {
    method: 'POST',
    credentials: 'include',
    cache: 'no-store',
    signal: handlers.signal,
    headers: withOnboardingRuntimeHeaders({
      accept: 'text/event-stream',
      'content-type': 'application/json',
    }),
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    await readOnboardingResponseError(response);
  }

  const responseBody = response.body;
  if (!responseBody) {
    throw new OnboardingRequestError(
      response.status,
      'Failed to read onboarding response stream'
    );
  }

  const reader = responseBody.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let dataLines: string[] = [];
  let completedResponse: OnboardingResponsesAPIResponse | null = null;

  const flush = () => {
    if (dataLines.length === 0) {
      return;
    }

    const data = dataLines.join('\n');
    dataLines = [];
    if (data === '[DONE]') {
      return;
    }

    const payload = parseSSEPayload(data);
    if (typeof payload !== 'object' || payload === null) {
      return;
    }

    const record = payload as {
      type?: string;
      delta?: unknown;
      response?: OnboardingResponsesAPIResponse;
      message?: unknown;
    };
    if (record.type === 'error') {
      throw new OnboardingRequestError(
        502,
        typeof record.message === 'string'
          ? record.message
          : 'Failed to create response'
      );
    }

    if (
      record.type === 'response.output_text.delta' &&
      typeof record.delta === 'string'
    ) {
      handlers.onTextDelta?.(record.delta);
      return;
    }

    if (record.type === 'response.completed') {
      completedResponse = record.response ?? null;
      handlers.onCompleted?.(completedResponse);
    }
  };

  const processLine = (rawLine: string) => {
    const line = rawLine.endsWith('\r') ? rawLine.slice(0, -1) : rawLine;
    if (line === '') {
      flush();
      return;
    }
    if (line.startsWith(':')) {
      return;
    }
    if (!line.startsWith('data:')) {
      return;
    }
    const value = line.slice(5).trimStart();
    dataLines.push(value);
  };

  for (;;) {
    // eslint-disable-next-line no-await-in-loop
    const { value, done } = await reader.read();
    if (done) {
      break;
    }

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split(/\n/);
    buffer = lines.pop() ?? '';
    for (const line of lines) {
      processLine(line);
    }
  }

  buffer += decoder.decode();
  if (buffer.length > 0) {
    processLine(buffer);
  }
  flush();

  return completedResponse;
}
